
export type ContractAccountStatus = 'Available' | 'Locked' | 'Deactivated';
export type ContractStatus = 'Pending' | 'Transferring' | 'Stopped' | 'Outsourcing' | 'Done' | 'Canceled' | 'Expired' | 'Failed';
export type PaymentStatus = 'Payed' | 'NotPayed';
export type CardStatus = 'Listed' | 'Sold' | 'Expired' | 'QuickSold' | 'LeftBehind';

export const CONTRACT_ACCOUNT_STATUS_OPTIONS: ContractAccountStatus[] = ['Available', 'Locked', 'Deactivated'];
export const CONTRACT_STATUS_OPTIONS: ContractStatus[] = ['Pending', 'Transferring', 'Stopped', 'Outsourcing', 'Done', 'Canceled', 'Expired', 'Failed'];

export interface ContractAccount {
  id: string;
  name: string;
  login_info_email: string;
  login_info_id: string;
  status: ContractAccountStatus;
  locked_by_contract_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContractIssue {
  type: 'monitor' | 'rate_limit' | 'transfer_market_limit';
  resume_at: string;
}

export interface BuyerAccountInfo {
  name: string;
  email: string;
  login_info_id: string;
}

export interface CardsSummary {
  total: number;
  listed: number;
  sold: number;
  expired: number;
  quick_sold: number;
  quick_sold_loss: number;
  left_behind: number;
}

export interface SellContract {
  id: string;
  name: string;
  note: string | null;
  buyer_id: string;
  seller_id: string;
  target_coins: number;
  target_type: string;
  status: ContractStatus;
  fee: number;
  /** Computed: fee * (matched_coins / 100_000) — read-only */
  fee_amount: number;
  payment_status: PaymentStatus;
  archived: boolean;
  in_pool: boolean;
  /** Admin flag: this contract's pool cards are bought first */
  priority: boolean;
  country?: string;
  started_at: string;
  expire_at: string;
  /** Present only when the contract has finished */
  finished_at?: string;
  /** When listing was paused; present while the contract is Stopped */
  stopped_at?: string;
  /** Set while a quick_sell_leftovers job is queued or running for the contract */
  quick_sell_started_at?: string;
  created_at: string;
  updated_at: string;
  estimated_sent_coins: number;
  estimated_received_coins: number;
  current_issue: ContractIssue | null;
  failed_reason: string | null;
  buyer_account_info?: BuyerAccountInfo;
  cards_summary?: CardsSummary;
}

export interface PublicSellContractResponse {
  id: string;
  name: string;
  target_coins: number;
  target_type: 'sent' | 'received';
  status: ContractStatus;
  fee: number;
  fee_amount: number;
  payment_status: PaymentStatus;
  started_at: string;
  expire_at: string;
  /** Present only when the contract has finished */
  finished_at?: string;
  /** When listing was paused; present while the contract is Stopped */
  stopped_at?: string;
  created_at: string;
  updated_at: string;
  estimated_sent_coins: number;
  estimated_received_coins: number;
  current_issue: ContractIssue | null;
  failed_reason: string | null;
}



/**
 * Shape of GET /player-auction/public/sell-contract/:id/report.
 *
 * The backend deliberately returns this in the old order-report schema (see
 * ToPublicSellContractCustomerReport), which is why the field names read like an
 * order's. It used to be derived from PublicOrderStatus with Omit<>; that type
 * went with the ORDER module, so the fields are spelled out here. `status` is the
 * raw (capitalized) contract status and `error_msg` carries the classified
 * failure-reason code, not free text.
 */
export interface ContractPublicOrderStatus {
  id: string;
  name: string;
  email?: string;
  target_coins: number;
  transferred_coins: number;
  start_credit: number;
  last_credit: number;
  order_date: string;
  status: ContractStatus;
  archived: boolean;
  error_msg: string;
  transfer_count: number;
  failed_transfer_count: number;
  created_at: string;
  updated_at: string;
}

/** Classified failed_reason codes that PATCH .../correct-credentials can resolve. */
export const CONTRACT_CORRECTABLE_REASONS = ['wrong_password', 'wrong_backup_code'] as const;

/** Classified failed_reason codes that PATCH .../resume can resolve. */
export const CONTRACT_RESUMABLE_REASONS = [
  'signed_into_another_device',
  'transfer_list_full',
  'not_enough_coin',
  'two_factor_not_enabled',
] as const;

export interface CorrectPublicSellContractCredentialsRequest {
  password?: string;
  backup_code_1?: string;
  backup_code_2?: string;
  backup_code_3?: string;
  backup_code_4?: string;
  backup_code_5?: string;
  backup_code_6?: string;
}

export interface ContractCard {
  id: string;
  trade_id_str: string;
  contract_id: string;
  player_id: number;
  player_name: string;
  player_nickname: string;
  player_image_path: string;
  buy_now_price: number;
  bid_price: number;
  quality: string;
  rarity_id: number;
  expire_time: string;
  quick_sell_price: number;
  cost: number;
  status: CardStatus;
}

// Contract Account API
export interface CreateContractAccountRequest {
  login_info_id: string;
  name: string;
}

export interface UpdateContractAccountRequest {
  name?: string;
  login_info_id?: string;
  status?: ContractAccountStatus;
}

// Sell Contract API
export interface CreateSellContractRequest {
  name: string;
  note?: string;
  account_id?: string;
  account_email?: string;
  target_coins: number;
  fee?: number;
  target_type?: 'sent' | 'received';
  in_pool?: boolean | null;
  country?: string;
}

export interface GetSellContractsRequest {
  pageIndex?: number;
  pageSize?: number;
  status?: ContractStatus;
  search?: string;
  archived?: boolean;
  in_pool?: boolean;
  quick_selling?: boolean;
  date_from?: string;
  date_to?: string;
}

export interface GetSellContractsResult {
  items: SellContract[];
  total: number;
  pageIndex: number;
  pageSize: number;
  totalPages: number;
}

export interface GetContractAccountListResult {
  items: ContractAccount[];
  total: number;
  pageIndex: number;
  pageSize: number;
  totalPages: number;
}

export interface getContractAccountsRequest {
  pageIndex?: number;
  pageSize?: number;
  status?: ContractAccountStatus,
  search?: string,
}

export interface UpdateSellContractRequest {
  name: string | null;
  note: string | null;
  target_coins: number | null;
}

export interface ArchiveSellContractsRequest {
  ids: string[];
  archived: boolean;
}

export interface QuickSellLeftoversRequest {
  ids: string[];
}

// Admin billing
export interface UpdateContractBillingRequest {
  fee?: number;
  payment_status?: PaymentStatus;
}

export interface AdminSellContractResponse extends SellContract {
  seller_username: string;
  seller_roles: string[];
}

/** Row shape usable for both the regular and admin sell-contract lists. */
export type SellContractRow = SellContract & Partial<Pick<AdminSellContractResponse, 'seller_username' | 'seller_roles'>>;

export interface GetAdminSellContractsRequest {
  pageIndex?: number;
  pageSize?: number;
  status?: ContractStatus;
  search?: string;
  ownerRole?: string;
  archived?: boolean;
  quick_selling?: boolean;
  date_from?: string;
  date_to?: string;
}

export interface GetAdminSellContractsResult {
  items: AdminSellContractResponse[];
  total: number;
  pageIndex: number;
  pageSize: number;
  totalPages: number;
}

// Outsourcing handoff (admin)
export type OutsourcingHandoffStatus =
  | 'Preparing'
  | 'Ready'
  | 'Submitting'
  | 'SubmissionUnknown'
  | 'Active'
  | 'Finalizing'
  | 'NeedsCredentials'
  | 'NeedsReview'
  | 'Completed'
  | 'Failed'
  | 'Canceled';

export const OUTSOURCING_HANDOFF_TERMINAL_STATUSES: readonly OutsourcingHandoffStatus[] = ['Completed', 'Failed', 'Canceled'];

export type OutsourcingActionRequired =
  | 'contract_status_changed'
  | 'review_provider_error'
  | 'review_provider_status'
  | 'partial_completion'
  | 'credentials_required'
  | 'transfer_slots_unavailable'
  | 'account_session_unavailable';

export interface OutsourcingHandoffResponse {
  id: string;
  sell_contract_id: string;
  status: OutsourcingHandoffStatus;
  provider: string;
  provider_reference: string;
  /** Omitted until submitted to the provider */
  provider_order_id?: string;
  local_sent_coins: number;
  local_received_coins: number;
  requested_coins: number;
  provider_sent_coins: number;
  provider_received_coins: number;
  free_slots_before_submit?: number;
  /** Provider's own words, omitempty */
  provider_status?: string;
  provider_account_state?: string;
  provider_economy_state?: string;
  /** Set only in NeedsReview/NeedsCredentials */
  action_required?: OutsourcingActionRequired | null;
  failure_reason?: string | null;
  /** Empty string (not omitted) when unset */
  next_poll_at: string;
  finished_at: string;
  created_at: string;
  updated_at: string;
}

export interface UpdateOutsourcingHandoffCredentialsRequest {
  password?: string;
  backup_codes?: string[];
}

// Public provider-order tracking (GET/POST /outsourcing/:orderID/*, no auth)
export type ProviderOrderStatus = 'processing' | 'resting' | 'stopped' | 'failed' | 'completed' | 'unknown';

export interface ProviderOrderStatusResponse {
  id: string;
  target_coins: number;
  transferred_coins: number;
  last_credit?: number;
  status: ProviderOrderStatus;
  error_msg?: string;
}

export interface ProviderOrderActionResponse {
  outcome: string;
}

export interface ProviderCredentialCorrectionRequest {
  password?: string;
  backup_codes?: string[];
}

// Stats report (shared shape between sell and buy contract stats-report endpoints)
export interface ContractLeftTarget {
  sent: number;
  received: number;
  total: number;
}

export interface GetSellContractStatsRequest {
  search?: string;
  archived?: boolean;
  in_pool?: boolean;
  date_from?: string;
  date_to?: string;
}

export interface SellContractStatsReport {
  total_contracts: number;
  status_counts: Partial<Record<ContractStatus, number>>;
  total_target: number;
  total_sent: number;
  total_received: number;
  total_fee: number;
  avg_fee: number;
  /** Remaining coins to hit target, summed only over open (Pending/Transferring/Stopped) contracts. */
  left_target: ContractLeftTarget;
}
/** What a set of finished sell contracts billed. unpaid = amount - paid. */
export interface SellContractDailyTotals {
  count: number;
  sent_coins: number;
  received_coins: number;
  amount: number;
  paid: number;
  unpaid: number;
}

export interface SellContractDailyRow extends SellContractDailyTotals {
  date: string;
}

export interface SellContractDailyReportResponse {
  timezone: string;
  rows: SellContractDailyRow[];
  /** The whole filtered range, not only this page. */
  totals: SellContractDailyTotals;
  total: number;
  pageIndex: number;
  pageSize: number;
  totalPages: number;
}
